import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

type ConsentState = 'undecided' | 'accepted' | 'declined';

export const STORAGE_KEY = 'cookie_consent';

/** Inject AdSense script dynamically — called only after consent */
function loadAdSense() {
  if (document.getElementById('adsense-script')) return; // already loaded
  const script = document.createElement('script');
  script.id = 'adsense-script';
  script.async = true;
  script.src =
    'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2698824285056948';
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

export default function CookieBanner() {
  const [state, setState] = useState<ConsentState>('undecided');
  const [showDetails, setShowDetails] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ConsentState | null;
    if (stored === 'accepted') {
      setState('accepted');
      loadAdSense(); // already consented — load ads immediately
    } else if (stored === 'declined') {
      setState('declined');
    } else {
      // Small delay so it slides in after page load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const saveConsent = async (choice: 'accepted' | 'declined') => {
    localStorage.setItem(STORAGE_KEY, choice);
    setState(choice);
    setVisible(false);

    if (choice === 'accepted') {
      loadAdSense();
    }

    // Submit to form endpoint (non-blocking)
    try {
      const body = new URLSearchParams();
      body.append('consent', choice);
      body.append('timestamp', new Date().toISOString());
      body.append('url', window.location.href);
      await fetch('https://readdy.ai/api/form/d7bsq8iqofe8vli40pcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Silent fail — consent is already saved locally
    }
  };

  if (state !== 'undecided' || !visible) return null;

  return (
    <>
      {/* Backdrop blur on mobile */}
      <div className="fixed inset-0 z-[90] bg-black/10 backdrop-blur-[1px] md:hidden pointer-events-none" />

      {/* Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-[100] transition-transform duration-500 ease-out ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-700">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-5">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">

              {/* Icon + text */}
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-500 flex-shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-lg" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 mb-0.5">
                    We use cookies to improve your experience
                  </p>
                  <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                    We and our partners use cookies for analytics, personalised ads (via Google AdSense), and to remember your preferences.
                    By clicking &ldquo;Accept All&rdquo; you consent to our use of cookies.{' '}
                    <Link
                      to="/privacy-policy"
                      className="text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 underline underline-offset-2 whitespace-nowrap"
                    >
                      Privacy Policy
                    </Link>
                  </p>

                  {/* Expandable details */}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="mt-1.5 text-xs text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 flex items-center gap-1 cursor-pointer"
                  >
                    <i className={`text-xs transition-transform duration-200 ${showDetails ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                    {showDetails ? 'Hide details' : 'Show cookie details'}
                  </button>

                  {showDetails && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {[
                        {
                          icon: 'ri-settings-3-line',
                          title: 'Essential',
                          desc: 'Required for the site to work. Cannot be disabled.',
                          always: true,
                        },
                        {
                          icon: 'ri-bar-chart-line',
                          title: 'Analytics',
                          desc: 'Help us understand how visitors use the site.',
                          always: false,
                        },
                        {
                          icon: 'ri-advertisement-line',
                          title: 'Advertising',
                          desc: 'Google AdSense personalised ads based on your interests.',
                          always: false,
                        },
                      ].map(({ icon, title, desc, always }) => (
                        <div
                          key={title}
                          className="flex items-start gap-2.5 bg-stone-50 dark:bg-stone-800 rounded-lg px-3 py-2.5"
                        >
                          <div className="w-5 h-5 flex items-center justify-center text-rose-400 flex-shrink-0 mt-0.5">
                            <i className={`${icon} text-sm`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className="text-xs font-semibold text-stone-700 dark:text-stone-200">{title}</span>
                              {always && (
                                <span className="text-xs text-stone-400 dark:text-stone-500 italic">(always on)</span>
                              )}
                            </div>
                            <p className="text-xs text-stone-400 dark:text-stone-500 leading-snug">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => saveConsent('declined')}
                  className="px-4 py-2.5 rounded-lg border border-stone-200 dark:border-stone-600 text-stone-600 dark:text-stone-300 text-sm font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Essential only
                </button>
                <button
                  onClick={() => saveConsent('accepted')}
                  className="px-5 py-2.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}
